import React from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight } from "lucide-react";
import { timeline, founders, beliefs } from "../mock/mock";

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">Our Story</p>
        <h1 className="font-serif-display text-5xl md:text-7xl font-bold text-navy mt-4 leading-tight">
          We Didn't Start a Business.<br />
          <span className="text-orange">We Started a Conversation.</span>
        </h1>
        <p className="font-serif-body text-2xl text-navy/80 mt-8 leading-relaxed">
          Two Kellys. One shared conviction: that running a business is hard, nobody tells you the truth about it,
          and having the right person in your corner changes everything. That's why BOSSY exists.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-5">
        <div className="rounded-2xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(22,38,63,0.4)]">
          <img
            src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a"
            alt="Kelly and Kelly"
            className="w-full h-[300px] md:h-[460px] object-cover"
          />
        </div>
      </section>

      {/* Story text */}
      <section className="max-w-3xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy mb-8">Our Story</h2>
        <div className="space-y-6 font-ui text-navy/80 text-lg leading-relaxed">
          <p>We didn't meet in a boardroom. We met in our 20s, then began working together at Hillside Children's Center — Kelly M. as a Special Education Teacher, Kelly B. as a Behavioral Specialist. The work was equal parts rewarding and relentless, and we found each other in the margins of it.</p>
          <p>After work, we'd debrief. Talk through the hard days, celebrate the small wins, and be honest in a way you can only be with someone who truly gets it. That feeling of being genuinely understood became the thing we kept chasing.</p>
          <p>Within a year of each other, we both left to open restaurants. The industry changed. The after-work conversations didn't — if anything, they got more necessary. Because nobody tells you what running a business is actually like. They see the Instagram version. We were living the real one.</p>
          <p>We felt so good after our meetings — clear, grounded, less alone — that we thought: what if other business owners could feel this too? That question became BOSSY.</p>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-14">The Journey</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-cream-dark -translate-x-1/2" />
            <div className="space-y-10">
              {timeline.map((t, i) => (
                <div key={t.year} className={`relative flex md:items-center gap-6 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-teal border-4 border-white -translate-x-1/2 z-10" />
                  <div className={`hidden md:block md:w-1/2 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`} />
                  <div className={`pl-12 md:pl-0 md:w-1/2 ${i % 2 === 0 ? "md:pl-12" : "md:pr-12 md:text-right"}`}>
                    <div className="font-serif-display text-2xl font-bold text-orange">{t.year}</div>
                    <p className="font-ui text-navy/75 mt-2 leading-relaxed">{t.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision (cleaned copy) */}
      <section className="max-w-4xl mx-auto px-5 py-20 text-center">
        <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase">Our Vision</p>
        <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-navy mt-4">Two People. One Mission.</h2>
        <p className="font-serif-body text-2xl text-navy/80 mt-8 leading-relaxed">
          We believe in transparency — sharing both the highs and the lows, every triumph and every setback. Our aim is
          to equip you with the tools and information you need to grow, alongside a community of women who are here to
          support and guide you on your journey as a business owner. Running a business can be challenging, and these
          conversations can stir strong emotions. We believe sharing our stories and experiences is essential to growth,
          and we honor the insights we gain from each other's journeys.
        </p>
      </section>

      {/* Meet the Kellys */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-14">Meet the Kellys</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {founders.map((f) => (
              <div key={f.name} className="bg-cream rounded-2xl overflow-hidden lift border" style={{ borderColor: "var(--cream-dark)" }}>
                <img src={f.img} alt={f.name} className="w-full h-72 object-cover" />
                <div className="p-7">
                  <h3 className="font-serif-display text-3xl font-bold text-navy">{f.name}</h3>
                  <a href={`mailto:${f.email}`} className="inline-flex items-center gap-2 text-teal font-ui text-sm mt-2 hover:text-orange transition-colors">
                    <Mail size={15} /> {f.email}
                  </a>
                  <p className="font-ui text-navy/75 mt-4 leading-relaxed text-[15px]">{f.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beliefs */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-4">What We Believe</h2>
        <p className="text-center font-serif-body text-2xl text-orange italic mb-14">The BOSSY Way.</p>
        <div className="grid md:grid-cols-3 gap-7">
          {beliefs.map((b, i) => (
            <div key={b.title} className="bg-white rounded-2xl p-8 border" style={{ borderColor: "var(--cream-dark)" }}>
              <div className="font-serif-display text-5xl font-bold text-teal/30">0{i + 1}</div>
              <h3 className="font-serif-display text-2xl font-semibold text-navy mt-3">{b.title}</h3>
              <p className="font-ui text-navy/70 mt-3 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-cream py-16">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold">Ready to stop figuring it out alone?</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/community" className="inline-flex items-center gap-2 bg-teal text-navy px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
              Join the Community <ArrowRight size={18} />
            </Link>
            <Link to="/podcast" className="inline-flex items-center gap-2 border border-white/40 px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-white/10 transition-colors">
              Listen to the Podcast
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
